<template>
  <div v-if="showFilters">
    <TransitionGroup name="chip-list" tag="div" class="d-flex flex-wrap ga-2">
      <novice-filter-chip
        v-for="config in sortedChipConfigs"
        :key="config.key"
        :chip-config="config"
        @pending-dismissed="pendingCustomKey = null"
      />
      <v-chip
        v-if="hasMoreFilters"
        key="__more-filters__"
        variant="text"
        label
        size="default"
        class="font-weight-regular"
        @click="showDialog = true"
      >
        more
      </v-chip>
    </TransitionGroup>

    <novice-filter-dialog
      v-model="showDialog"
      @select="onFilterSelected"
    />
  </div>
</template>

<script setup>
import { ref, computed, provide, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

import NoviceFilterChip from '@/components/NoviceFilterChip.vue';
import NoviceFilterDialog from '@/components/NoviceFilterDialog.vue';
import { url } from '@/url';
import { filtersFromUrlStr } from '@/filterConfigs';
import { getFacetConfig, facetsByCategory } from '@/facetConfigUtils';
import { facetTypeToChipType, defaultChipsByEntity, semanticDefaultChipConfigs } from '@/components/Filter/basicFilterMode';

defineOptions({ name: 'NoviceFilterChips' });

const props = defineProps({
  // Cap the bar to a FIXED number of chip slots (#440 r10): active chips
  // displace inactive defaults from the right so the bar never wraps. Passed by
  // the flag-on SERP (SerpInputContainer); default false keeps the flag-off
  // ExpertSerp's show-everything behavior unchanged.
  capToSlots: Boolean,
});

const store = useStore();
const route = useRoute();

const entityType = computed(() => store.getters.entityType);
const isSemanticSearch = computed(() => !!route.query['search.semantic']);

// Default chip sets live in basicFilterMode.js (#440 r10) so the chip-slot
// budget is shared with the SERP mode gate (SerpInputContainer).
const defaultChipConfigs = computed(() => {
  if (isSemanticSearch.value) return semanticDefaultChipConfigs;
  return defaultChipsByEntity[entityType.value] || [];
});

const DEFAULT_KEYS = computed(() => new Set(defaultChipConfigs.value.map(c => c.key)));

// --- Filter state (sourced from the server's canonical x_query, #378 C1) ---
// chipFilterStr returns the settled response's canonical filter once it has
// landed, falling back to route.query.filter while in flight. Reading the
// reactive store inside this computed keeps chips in sync as results settle.
const allUrlFilters = computed(() => {
  return filtersFromUrlStr(entityType.value, url.chipFilterStr(route));
});

const activeKeys = computed(() => {
  return new Set(allUrlFilters.value.map(f => f.key));
});

// --- Auto-open behavior (provide to child chips) ---
const autoOpenKey = ref(null);
provide('noviceAutoOpenKey', autoOpenKey);

// --- Pending chip (from dialog selection, before value is set) ---
const pendingCustomKey = ref(null);

// Clear pending when it gets a real URL value
watch(activeKeys, (keys) => {
  if (pendingCustomKey.value && keys.has(pendingCustomKey.value)) {
    pendingCustomKey.value = null;
  }
});

// --- Dialog state ---
const showDialog = ref(false);

// --- Build custom chip config from a facetConfig ---
function chipConfigFromFacetConfig(facetConfig, isPending = false) {
  if (!facetConfig) return null;
  const chipType = facetTypeToChipType(facetConfig);
  if (!chipType) return null;
  return {
    key: facetConfig.key,
    // Verbatim labels (e.g. the collection filter's "Work is in collection", #367)
    // are already sentence-cased; titleCase would wrongly capitalise every word.
    label: facetConfig.displayNameVerbatim ? facetConfig.displayName : titleCase(facetConfig.displayName),
    chipType,
    entityToSelect: facetConfig.entityToSelect,
    icon: facetConfig.icon,
    isCustom: true,
    isPending,
  };
}

function titleCase(str) {
  return str.replace(/\b\w/g, c => c.toUpperCase());
}

// --- Custom active chips (from URL, not in defaults) ---
const customActiveChipConfigs = computed(() => {
  const seen = new Set();
  return allUrlFilters.value
    .filter(f => !DEFAULT_KEYS.value.has(f.key))
    .filter(f => {
      if (seen.has(f.key)) return false;
      seen.add(f.key);
      return true;
    })
    .reverse() // most recently added first
    .map(f => chipConfigFromFacetConfig(getFacetConfig(entityType.value, f.key)))
    .filter(Boolean);
});

// --- Default chips split by active/inactive ---
const activeDefaultChips = computed(() =>
  defaultChipConfigs.value.filter(c => activeKeys.value.has(c.key))
);
const inactiveDefaultChips = computed(() =>
  defaultChipConfigs.value.filter(c => !activeKeys.value.has(c.key))
);

// --- Sorted chip list, capped to a FIXED number of slots (#440 r10) ---
// The bar always shows `slots` chips (the default set's size): active chips
// (custom + default) and the pending chip claim slots first, and inactive
// defaults fill whatever remains — i.e. each extra active chip displaces the
// rightmost inactive default, so the bar never wraps to a second row. When the
// active count outgrows the slots, the SERP forces advanced mode and disables
// Basic (SerpInputContainer's gate, driven by chipSlotCount — same budget).
const sortedChipConfigs = computed(() => {
  const slots = defaultChipConfigs.value.length;
  const configs = [
    ...customActiveChipConfigs.value,
    ...activeDefaultChips.value,
  ];

  // Add pending custom chip at the front if not already in the list
  if (pendingCustomKey.value && !configs.some(c => c.key === pendingCustomKey.value)) {
    const fc = getFacetConfig(entityType.value, pendingCustomKey.value);
    const pending = chipConfigFromFacetConfig(fc, true);
    if (pending) configs.unshift(pending);
  }

  // Inactive defaults fill only the slots the active/pending chips left over
  // (uncapped — flag-off — they're all appended, as always).
  const remaining = props.capToSlots
    ? Math.max(0, slots - configs.length)
    : inactiveDefaultChips.value.length;
  configs.push(...inactiveDefaultChips.value.slice(0, remaining));

  return configs;
});

// --- Minimum result count to show filters ---
const MIN_RESULTS_FOR_FILTERS = 100;
const resultsCount = computed(() => store.getters.resultsCount);

// Count filters available in the "more" dialog that aren't already shown as default chips
const dialogFilterKeys = computed(() => {
  const categories = facetsByCategory(
    entityType.value, '', ['selectEntity', 'boolean', 'range', 'search'], [],
  );
  const allDialogKeys = new Set();
  for (const cat of categories) {
    for (const fc of cat.filterConfigs) {
      if (fc.actions?.includes('filter') && fc.key !== 'is_xpac') {
        allDialogKeys.add(fc.key);
      }
    }
  }
  // Remove keys already shown as default chips
  for (const key of DEFAULT_KEYS.value) {
    allDialogKeys.delete(key);
  }
  return allDialogKeys;
});

const hasMoreFilters = computed(() => dialogFilterKeys.value.size > 0);

// Hide the entire filter bar when there are too few total results for that entity type.
// Always show if user has active filters or a search query (so they can modify/remove them).
const hasSearchQuery = computed(() => !!route.query.filter || !!route.query['search.semantic']);
const showFilters = computed(() => {
  if (activeKeys.value.size > 0) return true;
  if (hasSearchQuery.value) return true;
  if (resultsCount.value === undefined) return true; // still loading
  return resultsCount.value >= MIN_RESULTS_FOR_FILTERS;
});

// --- Handle filter selection from dialog ---
function onFilterSelected(key) {
  showDialog.value = false;

  const facetConfig = getFacetConfig(entityType.value, key);
  if (!facetConfig) return;

  if (facetConfig.type === 'boolean') {
    // Immediately apply boolean=true, no menu needed
    url.createFilter(entityType.value, key, true);
  } else if (DEFAULT_KEYS.value.has(key)) {
    // Default chip already on bar — just auto-open its menu
    setTimeout(() => {
      autoOpenKey.value = key;
    }, 300);
  } else {
    // Show pending chip after dialog closes, then auto-open its menu
    // Small delay to let v-dialog close transition complete
    setTimeout(() => {
      pendingCustomKey.value = key;
      autoOpenKey.value = key;
    }, 300);
  }
}
</script>

<style scoped>
.chip-list-move {
  transition: transform 0.3s ease;
}
.chip-list-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.chip-list-leave-active {
  transition: opacity 0.2s ease;
  position: absolute;
}
.chip-list-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}
.chip-list-leave-to {
  opacity: 0;
}
</style>
